import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import { Stack, Typography } from "@mui/material";
import { type FC } from "react";

import { PlatformIcons } from "~/components/UI/PlatformIcons";
import { SettledElsewhereMark } from "~/components/UI/SettledElsewhereMark";
import { MarketApy } from "~/feature/event/components/MarketCalculator/components/MarketApy";
import { MarketLowLiquidity } from "~/feature/event/components/MarketCalculator/components/MarketLowLiquidity";
import { TradeBonus } from "~/feature/event/components/MarketCalculator/components/TradeBonus";
import { getNoPriceText } from "~/feature/event/components/MarketCalculator/utils/getNoPriceText";
import { usePlatformTradeLink } from "~/feature/event/hooks/usePlatformTradeLink";
import {
  type ICalculatedData,
  useMarketCalculator,
} from "~/feature/event/providers/MarketCalculatorProvider";
import { PlatformClickPlacement } from "~/scripts/platformClick";
import { type IAggregatedEvent, type IMarket } from "~/types/event";
import { formatCents } from "~/utils/formatPrice";
import { formatNumber, formatPercentageValue } from "~/utils/formatValue";

import * as Styled from "./TradeOnPlatformCard.styles";

interface ITradeOnPlatformCardProps {
  market: IMarket;
  event: IAggregatedEvent;
  calculatedData: ICalculatedData;
  settledElsewhereWarning?: string | null;
}

export const TradeOnPlatformCard: FC<ITradeOnPlatformCardProps> = ({
  market,
  event,
  calculatedData,
  settledElsewhereWarning = null,
}) => {
  const { answer } = useMarketCalculator();
  const { href, platformName, handleClick } = usePlatformTradeLink(
    market,
    event,
    PlatformClickPlacement.Calculator
  );
  const { entryPrice, daysRemaining } = calculatedData;

  return (
    <Styled.Card href={href} target="_blank" onClick={handleClick}>
      <PlatformIcons platforms={market.platform} size={32} />
      <Stack sx={{ width: "100%", gap: 0.5 }}>
        <Styled.TitleLine>
          <Typography variant="body1" sx={{ fontWeight: 700 }}>
            Trade on {platformName}
          </Typography>
          {settledElsewhereWarning && (
            <SettledElsewhereMark tooltip={settledElsewhereWarning} />
          )}
        </Styled.TitleLine>
        <TradeBonus platform={market.platform} />
        <Styled.MainText
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: "11px", whiteSpace: "pre-wrap" }}
        >
          {entryPrice === null ? (
            <>
              {getNoPriceText(answer, platformName)}
              {"\n"}
            </>
          ) : (
            <>
              At <strong>{formatCents(entryPrice)}</strong> buys you{" "}
              <strong>
                {formatNumber(calculatedData.shares, { withDollar: false })}
              </strong>{" "}
              shares | Odds:{" "}
              <strong>{formatPercentageValue(calculatedData.impliedChance)}</strong>
              {"\n"}
              Total Payout: <strong>{formatNumber(calculatedData.grossPayout)}</strong>{" "}
              | Net Profit: <strong>{formatNumber(calculatedData.netProfit)}</strong>
              {"\n"}Multiplier:{" "}
              <strong>{calculatedData.multiplier.toFixed(2)}x</strong> | ROI:{" "}
              <strong>{formatPercentageValue(calculatedData.roi)}</strong>{" "}
              <MarketApy calculatedData={calculatedData} />
              {"\n"}
            </>
          )}
          <MarketLowLiquidity market={market} />
          {daysRemaining > 1 && (
            <>
              <strong>{daysRemaining}</strong> days to resolution
            </>
          )}
        </Styled.MainText>
      </Stack>
      <Styled.IconBox>
        <ArrowOutwardIcon fontSize="small" color="disabled" />
      </Styled.IconBox>
    </Styled.Card>
  );
};
